import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Dodatek, ImportBatch, MilnikyView, Smlouva, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { formatDatum, formatDen, formatKc, formatProcento } from '../lib/format.js';
import {
  REZIM_DPH_LABEL,
  SMLOUVA_TYP_LABEL,
  TRANSE_SPOUSTEC_LABEL,
  TRANSE_STAV_BARVA,
  TRANSE_STAV_LABEL,
} from '../lib/labels.js';
import { TranseEditor, soucetProcent, type TranseRadek } from '../components/TranseEditor.js';
import {
  Badge,
  Btn,
  Chyba,
  Dialog,
  Fakt,
  Input,
  Karta,
  Nacitani,
  Pole,
  Select,
  td,
  th,
} from '../components/ui.js';
import { PodpisPad } from '../components/PodpisPad.js';
import { CenikPolozkyPanel } from './import/CenikPolozky.js';

/** Detail smlouvy — hlavička, sekce Pozastávka (tabulka tranší s editací), ceník. */
export function SmlouvaDetailPage() {
  const { id = '' } = useParams();
  const { maFinance, maPravo } = useAuth();

  const {
    data: smlouva,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['smlouva', id],
    queryFn: () => api.get<Smlouva>(`/api/contracts/${id}`),
  });
  const { data: zakazka } = useQuery({
    queryKey: ['zakazka', smlouva?.zakazkaId],
    queryFn: () => api.get<Zakazka>(`/api/projects/${smlouva?.zakazkaId}`),
    enabled: !!smlouva && maPravo('projekty.read'),
  });

  if (isLoading) return <Nacitani />;
  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!smlouva) return null;

  const podepsano = !!smlouva.datumPodpisu;

  return (
    <>
      {/* Masthead: protistrana = kdo (titulek) + typ štítek, STAV razítko vpravo. */}
      <header className="flex flex-col-reverse items-start gap-3 border-b border-border pb-4 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text-secondary">
            <Link to="/smlouvy" className="underline underline-offset-2">
              Smlouvy
            </Link>{' '}
            / {smlouva.cislo ?? '(bez čísla)'}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold lg:text-2xl">{smlouva.protistrana}</h1>
            <Badge stav={smlouva.typ === 'hlavni' ? 'info' : 'neutral'}>
              {SMLOUVA_TYP_LABEL[smlouva.typ]}
            </Badge>
          </div>
          {zakazka && (
            <p className="text-text-secondary">
              Zakázka:{' '}
              <Link to={`/zakazky/${zakazka.id}`} className="underline underline-offset-2">
                {zakazka.cislo} — {zakazka.nazev}
              </Link>
            </p>
          )}
        </div>
        <div
          className={`shrink-0 rounded-xl px-4 py-2 text-center ${
            podepsano ? 'bg-success-bg text-success-text' : 'bg-warning-bg text-warning-text'
          }`}
        >
          <p className="text-xs font-bold tracking-wide uppercase opacity-80">Stav</p>
          <p className="text-lg font-bold">{podepsano ? 'podepsaná' : 'nepodepsaná'}</p>
          {podepsano && <p className="text-xs">{formatDatum(smlouva.datumPodpisu)}</p>}
        </div>
      </header>

      {/* Datový pohled, ne plochý formulář: hero (cena) nad mřížkou faktů. */}
      <section className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
        <div className="border-b border-border p-6">
          {maFinance ? (
            <>
              <p className="text-xs font-bold tracking-wide text-text-secondary uppercase">
                Cena (bez DPH)
              </p>
              <p className="ph-mask text-3xl font-bold text-text-heading">
                {formatKc(smlouva.cena)}
              </p>
              <p className="mt-1 text-sm text-text-secondary">cena Díla · netto bez DPH</p>
            </>
          ) : (
            <p className="text-lg font-semibold">Smlouva {smlouva.cislo ?? ''}</p>
          )}
        </div>
        <dl className="grid gap-x-8 gap-y-5 p-6 sm:grid-cols-2 lg:grid-cols-3">
          <Fakt
            nazev="Záruka"
            hodnota={smlouva.zarukaMesicu != null ? `${smlouva.zarukaMesicu} měsíců` : '—'}
          />
          <Fakt nazev="Režim DPH" hodnota={REZIM_DPH_LABEL[smlouva.rezimDph]} />
          <Fakt nazev="Veřejná zakázka" hodnota={smlouva.verejnaZakazka ? 'ano' : 'ne'} />
          {maFinance && smlouva.platebniPodminky ? (
            <Fakt nazev="Platební podmínky" hodnota={smlouva.platebniPodminky} celaSirka />
          ) : null}
        </dl>
      </section>

      <MilnikySekce smlouva={smlouva} />
      <PozastavkaSekce smlouva={smlouva} />
      <ProtokolySekce smlouva={smlouva} />
      <DodatkySekce smlouva={smlouva} />
      <CenikSekce smlouva={smlouva} />
    </>
  );
}

/**
 * Dodatky této smlouvy — patří k ní (navyšují cenu Díla → základ pozastávky
 * „vč. dodatků"). Sestavují se v sekci Dodatky (fronta), tady je jejich domov.
 */
function DodatkySekce({ smlouva }: { smlouva: Smlouva }) {
  const { maFinance, maPravo } = useAuth();
  const maPravoDodatky = maPravo('finance.write');
  const navigate = useNavigate();

  const { data: dodatky } = useQuery({
    queryKey: ['dodatky', { smlouvaId: smlouva.id }],
    queryFn: () => api.get<Dodatek[]>(`/api/dodatky?smlouvaId=${smlouva.id}`),
    enabled: maFinance,
  });

  if (!maFinance) return null;

  return (
    <Karta
      nadpis="Dodatky smlouvy"
      akce={
        maPravoDodatky ? (
          // Dodatky nejsou v menu — sestavují se odsud (nebo ze ZL v detailu
          // Změny přes „Zařadit do dodatku…"). Deep-link předvyplní zakázku.
          <Link
            to={`/dodatky?novy=1&zakazka=${smlouva.zakazkaId}`}
            className="inline-flex min-h-10 items-center rounded-lg bg-primary px-3.5 text-sm font-semibold text-white shadow-sm transition-colors duration-100 hover:bg-primary-hover"
          >
            Sestavit dodatek
          </Link>
        ) : undefined
      }
    >
      {dodatky && dodatky.length === 0 && (
        <p className="text-sm text-text-secondary">
          Zatím žádný dodatek. Sestavíte ho tlačítkem vpravo nahoře z podepsaných výnosových ZL;
          podpisem navýší cenu Díla (a tím základ pozastávky).
        </p>
      )}
      {dodatky && dodatky.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={th}>Číslo</th>
                <th className={th}>ZL</th>
                <th className={`${th} text-right`}>Σ (netto)</th>
                <th className={th}>Stav</th>
                <th className={th}>Podepsán</th>
              </tr>
            </thead>
            <tbody>
              {dodatky.map((d) => (
                <tr
                  key={d.id}
                  className="cursor-pointer hover:bg-bg"
                  onClick={() => void navigate(`/dodatky/${d.id}`)}
                >
                  <td className={`${td} font-semibold`}>
                    <Link
                      to={`/dodatky/${d.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary underline underline-offset-2"
                    >
                      {d.cislo ?? d.id}
                    </Link>
                  </td>
                  <td className={td}>{d.zmenoveListy.length}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>{formatKc(d.castka)}</td>
                  <td className={td}>
                    <Badge stav={d.podepsano ? 'success' : 'info'}>
                      {d.podepsano ? 'podepsaný' : 'nepodepsaný'}
                    </Badge>
                  </td>
                  <td className={td}>{formatDatum(d.datumPodpisu)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karta>
  );
}

/** Pozastávka: tabulka tranší (%, spouštěč, odklad, stav) s editací konfigurace. */
function PozastavkaSekce({ smlouva }: { smlouva: Smlouva }) {
  const { maFinance, maPravo } = useAuth();
  const queryClient = useQueryClient();
  const [edituji, setEdituji] = useState(false);
  const [predavam, setPredavam] = useState(false);
  const [transe, setTranse] = useState<TranseRadek[]>([]);

  const ulozit = useMutation({
    mutationFn: () =>
      api.put(`/api/contracts/${smlouva.id}/pozastavka`, {
        transe: transe.map((t) => ({
          procento: t.procento.replace(',', '.').trim(),
          spoustec: t.spoustec,
          odkladMesicu: t.odkladMesicu,
        })),
      }),
    onSuccess: () => {
      setEdituji(false);
      void queryClient.invalidateQueries({ queryKey: ['smlouva', smlouva.id] });
    },
  });

  const pozastavka = smlouva.pozastavka;

  const zacitEditovat = () => {
    setTranse(
      (pozastavka?.transe ?? []).map((t) => ({
        procento: t.procento ?? '',
        spoustec: t.spoustec,
        odkladMesicu: t.odkladMesicu,
      })),
    );
    setEdituji(true);
  };

  return (
    <Karta
      nadpis="Pozastávka"
      akce={
        maPravo('smlouvy.write') && pozastavka && !edituji ? (
          <span className="flex gap-2">
            <Btn varianta="secondary" onClick={() => setPredavam(true)}>
              Zaevidovat předání díla
            </Btn>
            <Btn varianta="secondary" onClick={zacitEditovat}>
              Upravit tranše
            </Btn>
          </span>
        ) : undefined
      }
    >
      {predavam && <PredaniDilaDialog smlouva={smlouva} onZavrit={() => setPredavam(false)} />}
      {!pozastavka && (
        <p className="text-text-secondary">Smlouva nemá konfigurovanou pozastávku.</p>
      )}

      {pozastavka && !edituji && (
        <>
          {maFinance && (
            <p className="mb-3 text-text-primary">
              Základ: <strong>{formatKc(pozastavka.zaklad)}</strong> (cena Díla vč. dodatků)
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Č.</th>
                  <th className={th}>Procento</th>
                  <th className={th}>Spouštěč</th>
                  <th className={th}>Odklad</th>
                  <th className={th}>Stav</th>
                  {maFinance && <th className={th}>Částka (bez DPH)</th>}
                </tr>
              </thead>
              <tbody>
                {pozastavka.transe.map((t) => (
                  <tr key={t.id}>
                    <td className={td}>{t.poradi}.</td>
                    <td className={td}>{formatProcento(t.procento)}</td>
                    <td className={td}>{TRANSE_SPOUSTEC_LABEL[t.spoustec]}</td>
                    <td className={td}>
                      {t.odkladMesicu === 0 ? 'bez odkladu' : `${t.odkladMesicu} měsíců`}
                    </td>
                    <td className={td}>
                      <Badge barva={TRANSE_STAV_BARVA[t.stav]}>{TRANSE_STAV_LABEL[t.stav]}</Badge>
                    </td>
                    {maFinance && <td className={td}>{formatKc(t.castka)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pozastavka && edituji && (
        <div className="flex flex-col gap-4">
          <TranseEditor transe={transe} onChange={setTranse} />
          <p className="text-sm text-text-secondary">
            Součet tranší: {soucetProcent(transe)} % ze základu. Uložením se konfigurace tranší
            nahradí; částky se dopočtou z ceny smlouvy.
          </p>
          {ulozit.error ? <Chyba>{hlaskaZChyby(ulozit.error)}</Chyba> : null}
          <div className="flex gap-3">
            <Btn disabled={ulozit.isPending} onClick={() => ulozit.mutate()}>
              {ulozit.isPending ? 'Ukládám…' : 'Uložit tranše'}
            </Btn>
            <Btn varianta="secondary" onClick={() => setEdituji(false)}>
              Zrušit
            </Btn>
          </div>
        </div>
      )}
    </Karta>
  );
}

/** Ceník smlouvy — verze importů soupisu prací (platný/archivní) + obsah. */
function CenikSekce({ smlouva }: { smlouva: Smlouva }) {
  const { maPravo } = useAuth();
  const smiCist = maPravo('import.read');
  // Klik na verzi otevře její POLOŽKY (obsah ceníku) — nejen metadata souboru.
  const [otevrenyBatchId, setOtevrenyBatchId] = useState<string | null>(null);

  const { data: batche } = useQuery({
    queryKey: ['importy', { smlouvaId: smlouva.id }],
    queryFn: () => api.get<ImportBatch[]>(`/api/imports?smlouvaId=${smlouva.id}`),
    enabled: smiCist,
  });

  // Platný ceník rovnou otevřený — obsah je to hlavní, ne název souboru.
  const aktivniBatchId =
    otevrenyBatchId && batche?.some((b) => b.id === otevrenyBatchId)
      ? otevrenyBatchId
      : (batche?.find((b) => b.jePlatny)?.id ?? null);

  if (!smiCist) return null;

  return (
    <Karta
      nadpis="Ceník (soupis prací)"
      akce={
        maPravo('import.write') ? (
          // Import patří ke smlouvě — wizard se otevře s předvyplněnou
          // zakázkou i smlouvou (v menu už samostatná položka není).
          <Link
            to={`/import?novy=1&zakazka=${smlouva.zakazkaId}&smlouva=${smlouva.id}`}
            className="inline-flex min-h-10 items-center rounded-lg bg-primary px-3.5 text-sm font-semibold text-white shadow-sm transition-colors duration-100 hover:bg-primary-hover"
          >
            Importovat soupis prací
          </Link>
        ) : undefined
      }
    >
      {batche && batche.length === 0 && (
        <p className="text-text-secondary">
          Ke smlouvě zatím nebyl importován soupis prací. Ceník je read-only — vznikne tlačítkem
          „Importovat soupis prací“ vpravo nahoře.
        </p>
      )}
      {batche && batche.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Soubor</th>
                  <th className={th}>Kdy</th>
                  <th className={th}>Kdo</th>
                  <th className={th}>Položek</th>
                  <th className={th}>Verze</th>
                </tr>
              </thead>
              <tbody>
                {batche.map((b) => (
                  <tr
                    key={b.id}
                    className={`cursor-pointer ${
                      b.id === aktivniBatchId ? 'bg-primary-tint' : 'hover:bg-bg'
                    }`}
                    onClick={() => setOtevrenyBatchId(b.id)}
                  >
                    <td className={`${td} font-medium`}>{b.nazevSouboru}</td>
                    <td className={td}>{formatDatum(b.kdy)}</td>
                    <td className={td}>{b.kdo.jmeno}</td>
                    <td className={td}>{b._count.polozky}</td>
                    <td className={td}>
                      <Badge stav={b.jePlatny ? 'success' : 'neutral'}>
                        {b.jePlatny ? 'platný' : 'archivní'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Obsah vybrané verze — položky s cenami (to hlavní, co ceník je). */}
          {aktivniBatchId && <CenikPolozkyPanel batchId={aktivniBatchId} />}
        </div>
      )}
    </Karta>
  );
}

/**
 * Zjišťovací protokoly smlouvy — měsíční čerpání (návrh → odsouhlasení →
 * podklad faktury). Nástroj s jedním rodičem: žije u smlouvy, ne v menu.
 */
function ProtokolySekce({ smlouva }: { smlouva: Smlouva }) {
  const navigate = useNavigate();
  const { maPravo } = useAuth();
  const [chyba, setChyba] = useState<string | null>(null);
  const { data: protokoly } = useQuery({
    queryKey: ['protokoly', smlouva.id],
    queryFn: () =>
      api.get<{ id: string; obdobi: string; stav: string; suma: string }[]>(
        `/api/protokoly?smlouvaId=${smlouva.id}`,
      ),
    enabled: maPravo('finance.read'),
  });
  const zaloz = useMutation({
    mutationFn: () => {
      const ted = new Date();
      const obdobi = `${ted.getFullYear()}-${String(ted.getMonth() + 1).padStart(2, '0')}`;
      return api.post<{ id: string }>('/api/protokoly', { smlouvaId: smlouva.id, obdobi });
    },
    onSuccess: (p) => void navigate(`/protokoly/${p.id}`),
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });
  if (!maPravo('finance.read')) return null;
  return (
    <Karta
      nadpis="Zjišťovací protokoly"
      akce={
        maPravo('finance.write') ? (
          <Btn
            size="desktop"
            varianta="secondary"
            disabled={zaloz.isPending}
            onClick={() => zaloz.mutate()}
          >
            {zaloz.isPending ? 'Zakládám…' : 'Nový protokol (tento měsíc)'}
          </Btn>
        ) : undefined
      }
    >
      {chyba && <Chyba>{chyba}</Chyba>}
      {(protokoly ?? []).length === 0 ? (
        <p className="text-sm text-text-secondary">
          Zatím žádný — měsíční čerpání proti soupisu založíte tlačítkem vpravo. Odsouhlasený
          protokol je podklad k faktuře.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {(protokoly ?? []).map((p) => (
            <li key={p.id}>
              <Link
                to={`/protokoly/${p.id}`}
                className="flex min-h-11 items-center justify-between gap-3 py-1.5 hover:bg-bg"
              >
                <span className="font-semibold">{p.obdobi}</span>
                <span className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">{formatKc(p.suma)}</span>
                  <Badge stav={p.stav === 'odsouhlaseno' ? 'success' : 'warning'}>
                    {p.stav === 'odsouhlaseno' ? 'odsouhlasen' : 'návrh'}
                  </Badge>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Karta>
  );
}

/**
 * Předávací protokol — spouštěč tranší pozastávky: datum předání dopočte
 * termíny tranší (predani_dila + odklad). Podpis povinný (akt předání).
 */
function PredaniDilaDialog({ smlouva, onZavrit }: { smlouva: Smlouva; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  // Lokální den, ne UTC — večer by toISOString nabídl zítřejší datum předání.
  const ted = new Date();
  const dnesIso = `${ted.getFullYear()}-${String(ted.getMonth() + 1).padStart(2, '0')}-${String(
    ted.getDate(),
  ).padStart(2, '0')}`;
  const [datum, setDatum] = useState(dnesIso);
  const [castka, setCastka] = useState(smlouva.cena ?? '');
  const [ucastnici, setUcastnici] = useState('');
  const [obrazek, setObrazek] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);

  const zaloz = useMutation({
    mutationFn: () =>
      api.post('/api/contracts/predavaci-protokoly', {
        smlouvaId: smlouva.id,
        datum,
        castkaPredani: String(castka)
          .replace(',', '.')
          .replace(/[\s\u00a0]/g, ''),
        ucastnici: ucastnici.trim() || null,
        podpis: { obrazek, jmeno: me?.jmeno ?? '' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['smlouva', smlouva.id] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis="Zaevidovat předání díla" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <p className="text-base">
          Datum předání dopočte termíny tranší pozastávky (předání díla + odklad) — hlídač pak
          připomene, až bude tranše uvolnitelná.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Pole popisek="Datum předání" povinne>
            <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
          </Pole>
          <Pole popisek="Částka předání (Kč bez DPH)" povinne>
            <Input
              inputMode="decimal"
              value={castka ?? ''}
              onChange={(e) => setCastka(e.target.value)}
            />
          </Pole>
        </div>
        <Pole popisek="Účastníci předání">
          <Input
            placeholder="za GD Novák, za investora Dvořák (TDI)"
            value={ucastnici}
            onChange={(e) => setUcastnici(e.target.value)}
          />
        </Pole>
        <PodpisPad onChange={setObrazek} />
        <p className="-mt-2 text-sm text-text-secondary">Podepisuje: {me?.jmeno}</p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={zaloz.isPending || !obrazek || !datum || !castka}
            onClick={() => zaloz.mutate()}
          >
            {zaloz.isPending ? 'Zapisuji…' : 'Zaevidovat předání'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Milníky smlouvy — termíny s penále a NÁROK NA PRODLOUŽENÍ: efektivní termín
 * = smluvní + Σ dopadů schválených změn (jen hlavní SOD). Penále je volitelná
 * struktura (Kč/den × % z ceny Díla/den, strop) — každá firma to má jinak.
 */
function MilnikySekce({ smlouva }: { smlouva: Smlouva }) {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'novy' | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['milniky', smlouva.id],
    queryFn: () => api.get<MilnikyView>(`/api/contracts/${smlouva.id}/milniky`),
  });

  const smaz = useMutation({
    mutationFn: (milnikId: string) =>
      api.del<MilnikyView>(`/api/contracts/${smlouva.id}/milniky/${milnikId}`),
    onSuccess: (novy) => queryClient.setQueryData(['milniky', smlouva.id], novy),
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const popisPenale = (m: MilnikyView['milniky'][number]) => {
    if (!m.penaleTyp) return 'bez penále';
    const sazba =
      m.penaleTyp === 'kc_den'
        ? `${formatKc(m.penaleSazba)}/den`
        : `${String(m.penaleSazba).replace('.', ',')} % z ceny Díla/den` +
          (m.penaleDenniKc ? ` (≈ ${formatKc(m.penaleDenniKc)}/den)` : '');
    return m.penaleStrop ? `${sazba}, max ${formatKc(m.penaleStrop)}` : sazba;
  };

  return (
    <Karta
      nadpis="Milníky a penále"
      akce={
        maPravo('smlouvy.write') ? (
          <Btn size="desktop" varianta="secondary" onClick={() => setDialog('novy')}>
            Přidat milník
          </Btn>
        ) : undefined
      }
    >
      {chyba && <Chyba>{chyba}</Chyba>}
      {data && data.krytoDni > 0 && (
        <p className="mb-2 rounded-lg bg-info-bg p-2 text-sm text-info-text">
          Schválené změny kryjí prodloužení o <strong>{data.krytoDni} dní</strong> — termíny níže
          se o ně posouvají.
        </p>
      )}
      {(data?.milniky ?? []).length === 0 ? (
        <p className="text-sm text-text-secondary">
          Zatím žádný — zapište termíny ze SOD, na kterých visí penále. Hlídač pak upozorní
          předem a doloží nárok na prodloužení ze schválených změn.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {(data?.milniky ?? []).map((m) => (
            <li key={m.id} className="flex min-h-11 items-center justify-between gap-3 py-1.5">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{m.nazev}</span>
                <span className="block text-sm text-text-secondary">{popisPenale(m)}</span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <span className="text-sm tabular-nums">
                  {m.terminSKrytim !== m.termin ? (
                    <>
                      <s className="text-text-muted">{formatDen(m.termin)}</s>{' '}
                      <strong>{formatDen(m.terminSKrytim)}</strong>
                    </>
                  ) : (
                    formatDen(m.termin)
                  )}
                </span>
                {maPravo('smlouvy.write') && (
                  <button
                    type="button"
                    className="text-sm text-text-secondary underline underline-offset-2 hover:text-error-text"
                    onClick={() => smaz.mutate(m.id)}
                  >
                    smazat
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {dialog === 'novy' && (
        <MilnikDialog smlouva={smlouva} onZavrit={() => setDialog(null)} />
      )}
    </Karta>
  );
}

function MilnikDialog({ smlouva, onZavrit }: { smlouva: Smlouva; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const [nazev, setNazev] = useState('');
  const [termin, setTermin] = useState('');
  const [penaleTyp, setPenaleTyp] = useState<'' | 'kc_den' | 'procento_den'>('');
  const [sazba, setSazba] = useState('');
  const [strop, setStrop] = useState('');

  const uloz = useMutation({
    mutationFn: () =>
      api.post<MilnikyView>(`/api/contracts/${smlouva.id}/milniky`, {
        nazev,
        termin,
        penaleTyp: penaleTyp || null,
        ...(penaleTyp ? { penaleSazba: sazba } : {}),
        ...(penaleTyp && strop.trim() ? { penaleStrop: strop } : {}),
      }),
    onSuccess: (novy) => {
      queryClient.setQueryData(['milniky', smlouva.id], novy);
      onZavrit();
    },
  });

  return (
    <Dialog
      nadpis="Nový milník"
      onZavrit={onZavrit}
      akce={
        <Btn
          size="desktop"
          disabled={!nazev.trim() || !termin || (!!penaleTyp && !sazba.trim()) || uloz.isPending}
          onClick={() => uloz.mutate()}
        >
          {uloz.isPending ? 'Ukládám…' : 'Uložit milník'}
        </Btn>
      }
    >
      <div className="flex flex-col gap-3">
        <Pole popisek="Název (např. Dokončení hrubé stavby)">
          <Input value={nazev} onChange={(e) => setNazev(e.target.value)} autoFocus />
        </Pole>
        <Pole popisek="Smluvní termín">
          <Input type="date" value={termin} onChange={(e) => setTermin(e.target.value)} />
        </Pole>
        <Pole popisek="Penále za prodlení (dle vaší SOD)">
          <Select
            value={penaleTyp}
            onChange={(e) => setPenaleTyp(e.target.value as typeof penaleTyp)}
          >
            <option value="">Bez penále</option>
            <option value="kc_den">Pevná částka Kč za den</option>
            <option value="procento_den">% z ceny Díla za den</option>
          </Select>
        </Pole>
        {penaleTyp && (
          <div className="grid grid-cols-2 gap-3">
            <Pole popisek={penaleTyp === 'kc_den' ? 'Sazba (Kč/den)' : 'Sazba (%/den)'}>
              <Input
                inputMode="decimal"
                value={sazba}
                onChange={(e) => setSazba(e.target.value)}
                placeholder={penaleTyp === 'kc_den' ? 'např. 25000' : 'např. 0,05'}
              />
            </Pole>
            <Pole popisek="Strop celkem (Kč, nepovinné)">
              <Input inputMode="decimal" value={strop} onChange={(e) => setStrop(e.target.value)} />
            </Pole>
          </div>
        )}
        {uloz.error ? <Chyba>{hlaskaZChyby(uloz.error)}</Chyba> : null}
      </div>
    </Dialog>
  );
}
